import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { FileUploader } from 'react-drag-drop-files';
import { fileTypes, API_URL_TENDERS } from '../configuration/Configuration';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useEffect, useState } from 'react';
import axios from 'axios';
import loadFile from '../utils/updateLanesCSV';
import { useAuth } from '../context/AuthContext';

const Import = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tenderId = searchParams.get('id');
  const [tender, setTender] = useState();
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const { userType } = useAuth();

  useEffect(() => {
    if (tenderId) {
      axios.get(`${API_URL_TENDERS}${tenderId}`).then((res) => {
        setTender(res.data[0]);
      });
    }
  }, []);

  const handleFileChange = async (file) => {
    setIsUploading(true);
    setUploadResult(null);
    setUploadError(null);

    try {
      const result = await loadFile(file, tenderId);
      console.log(result);
      setUploadResult(result);

      // Redirect to lanes list view after successful upload for department users
      if (!userType?.isAdmin) {
        // Wait a brief moment to show the success message before redirecting
        setTimeout(() => {
          navigate(`/AdvanceTender?tender_id=${tenderId}`);
        }, 1500);
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Upload failed';
      setUploadError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Typography variant="h6" align="left">
        {userType?.isAdmin ? 'Update lane information' : 'Update lane pricing'}
      </Typography>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Typography align="left">
          <strong>
            Updating the lane information for tender ID {tender?.id}, company{' '}
            {tender?.company}
          </strong>
        </Typography>

        {userType?.isAdmin ? (
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Administrator permissions:</strong> You can update all
              lane fields including:
              <br />• Location fields (origin/destination countries, cities, ZIP
              codes)
              <br />• Lane properties (equipment, payload, transit time,
              requirements)
              <br />• Pricing fields (capacity, regional pricing, additional
              costs, margin)
              <br />• Create new lanes (add rows to the Excel)
              <br />• Delete lanes (remove rows from the Excel — a warning is
              shown for lanes with confirmed department prices)
            </Typography>
          </Alert>
        ) : (
          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Department user permissions:</strong> You can only update
              pricing fields:
              <br />• Department-specific pricing columns
              <br />• Capacity, transit time, additional costs, margin
              <br />• You cannot update loading locations or create new lanes
            </Typography>
          </Alert>
        )}

        {userType?.isAdmin && (
          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Upload the complete lane list:</strong> export the tender
              first, make your changes, and upload every lane again. Rows
              missing from the file are removed from the tender.
            </Typography>
          </Alert>
        )}

        <FileUploader
          handleChange={handleFileChange}
          name="file"
          types={fileTypes}
        />

        {isUploading && (
          <Alert severity="info">
            <Typography variant="body2">Uploading file...</Typography>
          </Alert>
        )}

        {uploadResult && (
          <>
            <Alert severity="success">
              <Typography variant="body2">
                {uploadResult.message || 'File uploaded successfully'}
              </Typography>
            </Alert>
            <Box sx={{ display: 'flex' }}>
              <Button
                onClick={() => navigate(`/AdvanceTender?tender_id=${tenderId}`)}
                variant="outlined"
                size="large"
              >
                Back to Manage Tender
              </Button>
            </Box>
          </>
        )}

        {uploadResult?.warnings?.length > 0 && (
          <Alert severity="warning">
            <Typography variant="body2">
              <strong>
                The following removed lanes had confirmed department prices:
              </strong>
            </Typography>
            {uploadResult.warnings.map((w, i) => (
              <Typography key={i} variant="body2">
                Lane {w.lane_id} ({w.route}) — confirmed by:{' '}
                {w.departments.join(', ') || 'unknown'}
              </Typography>
            ))}
          </Alert>
        )}

        {uploadError && (
          <Alert severity="error">
            <Typography variant="body2">{uploadError}</Typography>
          </Alert>
        )}
      </div>
    </Box>
  );
};

export default Import;
